import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Switch, Typography, Box, TextField, Tooltip, CircularProgress, Button,
  Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ScienceIcon from '@mui/icons-material/Science';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Home from './Home';
import FormulaireModeleDialog from './FormulaireModeleDialog';
import FormulairesGuideDialog from './FormulairesGuideDialog';
import TesterFormulaireDialog from '../formulaires/TesterFormulaireDialog';
import useIsMobile from '../hooks/useIsMobile';
import {
  Modele, ModeleResume, EtatFormulaires,
  listerModeles, etatFormulaires, getModele, majModele, supprimerModele, dupliquerModele,
  messageErreurs, codeHttp
} from '../services/formulairesServices';

// Écran « Formulaires » : liste des modèles du constructeur de formulaires (formulaires_modeles) et accès au Dialog
// d'édition. Les instances jointes aux dossiers sont gérées depuis la fiche du dossier (lot 5), pas ici.
const FormulairesScreen: React.FC = () => {
  const isMobile = useIsMobile();
  const [modeles, setModeles] = useState<ModeleResume[]>([]);
  const [etat, setEtat] = useState<EtatFormulaires | null>(null);
  // 404 sur /formulaires/etat : le backend admin tourne encore sans le module (pas redémarré depuis le déploiement)
  const [backendSansModule, setBackendSansModule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Modele | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chargementEdit, setChargementEdit] = useState<number | null>(null);
  // Aperçu interactif « Tester » (lot 4b) : modèle complet relu, puis Dialog sans aucune écriture
  const [modeleTest, setModeleTest] = useState<Modele | null>(null);
  const [testOuvert, setTestOuvert] = useState(false);
  const [guideOuvert, setGuideOuvert] = useState(false); // guide « créer et envoyer un formulaire »
  const [chargementTest, setChargementTest] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ModeleResume | null>(null);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  // Les deux appels sont indépendants : un 503 « tables absentes » sur la liste ne doit pas priver de /etat (bannière rouge)
  const fetchAll = async () => {
    setLoading(true);
    const [m, e] = await Promise.allSettled([listerModeles(), etatFormulaires()]);
    if (e.status === 'fulfilled') {
      setEtat(e.value);
      setBackendSansModule(false);
    } else {
      console.error('Erreur chargement état formulaires', e.reason);
      setEtat(null);
      setBackendSansModule(codeHttp(e.reason) === 404);
    }
    if (m.status === 'fulfilled') setModeles(m.value);
    else {
      console.error('Erreur chargement modèles', m.reason);
      setModeles([]);
      // pas de toast redondant si une bannière explique déjà (tables absentes, module absent)
      const explique = (e.status === 'fulfilled' && !e.value.ok) || codeHttp(m.reason) === 404;
      if (!explique) setToast({ msg: messageErreurs(m.reason, 'Chargement des formulaires impossible'), sev: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return modeles;
    return modeles.filter(
      (m) => m.nom.toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q)
    );
  }, [modeles, search]);

  const toggleActif = async (m: ModeleResume, actif: boolean) => {
    // maj optimiste, rollback en cas d'échec
    setModeles((prev) => prev.map((x) => (x.id === m.id ? { ...x, actif: actif ? 1 : 0 } : x)));
    try {
      await majModele(m.id, { actif });
    } catch (e) {
      console.error('Erreur toggle actif', e);
      setModeles((prev) => prev.map((x) => (x.id === m.id ? { ...x, actif: actif ? 0 : 1 } : x)));
      setToast({ msg: messageErreurs(e, 'Modification impossible'), sev: 'error' });
    }
  };

  const openCreate = () => {
    setSelected(null);
    setDialogOpen(true);
  };

  // La liste ne porte pas la définition : on relit le modèle complet avant d'ouvrir l'éditeur
  const openEdit = async (m: ModeleResume) => {
    setChargementEdit(m.id);
    try {
      const complet = await getModele(m.id);
      setSelected(complet);
      setDialogOpen(true);
    } catch (e) {
      console.error('Erreur lecture modèle', e);
      setToast({ msg: messageErreurs(e, 'Lecture du formulaire impossible'), sev: 'error' });
    } finally {
      setChargementEdit(null);
    }
  };

  // Même patron qu'openEdit : la liste ne porte pas la définition
  const openTest = async (m: ModeleResume) => {
    setChargementTest(m.id);
    try {
      const complet = await getModele(m.id);
      // definition vaut null si le JSON en base est illisible (routes.js) : ne pas ouvrir un test qui planterait
      if (!complet.definition || !Array.isArray(complet.definition.sections)) {
        setToast({ msg: 'Définition du formulaire illisible : ouvrez-le avec « Modifier »', sev: 'error' });
        return;
      }
      setModeleTest(complet);
      setTestOuvert(true);
    } catch (e) {
      console.error('Erreur lecture modèle (test)', e);
      setToast({ msg: messageErreurs(e, 'Lecture du formulaire impossible'), sev: 'error' });
    } finally {
      setChargementTest(null);
    }
  };

  const handleSaved = (m: Modele) => {
    setToast({ msg: `Formulaire « ${m.nom} » enregistré (v${m.version})`, sev: 'success' });
    fetchAll();
  };

  const handleDupliquer = async (m: ModeleResume) => {
    try {
      const copie = await dupliquerModele(m.id);
      setToast({ msg: `Formulaire « ${copie.nom} » créé (inactif)`, sev: 'success' });
      fetchAll();
    } catch (e) {
      console.error('Erreur duplication modèle', e);
      setToast({ msg: messageErreurs(e, 'Duplication impossible'), sev: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const m = confirmDelete;
    setConfirmDelete(null);
    try {
      await supprimerModele(m.id);
      setToast({ msg: `Formulaire « ${m.nom} » supprimé`, sev: 'success' });
      fetchAll();
    } catch (e) {
      console.error('Erreur suppression modèle', e);
      setToast({ msg: messageErreurs(e, 'Suppression impossible'), sev: 'error' });
    }
  };

  const indisponible = backendSansModule || (!!etat && !etat.ok);

  return (
    <>
      <Home />
      <Container maxWidth={false} sx={{ mt: 2, px: { xs: 1, sm: 3 } }}>
        {/* Bannières d'état du module */}
        {!loading && backendSansModule && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Le backend admin n'expose pas encore /formulaires : redémarrez-le.
          </Alert>
        )}
        {!loading && !backendSansModule && !etat && (
          <Alert severity="error" sx={{ mb: 2 }}>
            État du module Formulaires indisponible (backend injoignable ?).
          </Alert>
        )}
        {/* raison fournie par le backend : consigne de migration (tables absentes) ou erreur MySQL */}
        {etat && !etat.ok && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Module Formulaires désactivé : {etat.raison || 'raison inconnue'}.
            Le module se réactive seul dès que les tables existent.
          </Alert>
        )}
        {etat && etat.ok && etat.dry_run && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            MODE SIMULATION — aucun mail réel n'est envoyé aux clients (FORMULAIRES_MAIL_DRY_RUN).
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Formulaires</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Modèles de formulaires à joindre aux dossiers créés manuellement : le client les remplit en ligne depuis le
            lien reçu par mail. Modifier un modèle crée une nouvelle version ; les formulaires déjà envoyés gardent la leur.
          </Typography>
          <TextField
            label="Rechercher"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="outlined" startIcon={<HelpOutlineIcon />} onClick={() => setGuideOuvert(true)}>
            Guide
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={indisponible}>
            Nouveau formulaire
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Questions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Version</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Dossiers</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actif</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((m) => (
                  <TableRow key={m.id} hover sx={{ opacity: Number(m.actif) === 1 ? 1 : 0.6 }}>
                    <TableCell sx={{ fontWeight: 500, minWidth: 180 }}>
                      {m.nom}
                      {m.description && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {m.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {m.nb_questions === 0 ? (
                        <Tooltip title="Aucune question (hors paragraphes d'information)">
                          <Typography component="span" sx={{ fontWeight: 'bold', color: 'error.main' }}>0</Typography>
                        </Tooltip>
                      ) : (
                        <Typography component="span" sx={{ fontWeight: 'bold' }}>{m.nb_questions}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>v{m.version}</TableCell>
                    <TableCell align="center">
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>{m.nb_instances}</Typography>
                      {m.nb_instances_versions_anterieures > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: 'warning.main', whiteSpace: 'nowrap' }}>
                          dont {m.nb_instances_versions_anterieures} sur une version antérieure
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={Number(m.actif) === 1 ? 'Proposé dans « Nouveau dossier »' : 'Inactif : non proposé'}>
                        <Switch
                          size="small"
                          checked={Number(m.actif) === 1}
                          onChange={(e) => toggleActif(m, e.target.checked)}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Modifier">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={chargementEdit !== null}
                            onClick={() => openEdit(m)}
                          >
                            {chargementEdit === m.id ? <CircularProgress size={18} /> : <EditIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Tester le formulaire (rien n'est enregistré)">
                        <span>
                          <IconButton
                            size="small"
                            disabled={chargementTest !== null}
                            onClick={() => openTest(m)}
                          >
                            {chargementTest === m.id ? <CircularProgress size={18} /> : <ScienceIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Dupliquer">
                        <IconButton size="small" onClick={() => handleDupliquer(m)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error" onClick={() => setConfirmDelete(m)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      {modeles.length === 0 ? (
                        <>
                          Aucun formulaire pour l'instant.{' '}
                          <Button size="small" onClick={() => setGuideOuvert(true)} sx={{ textTransform: 'none' }}>
                            Consulter le guide
                          </Button>
                        </>
                      ) : 'Aucun formulaire ne correspond à la recherche'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      <FormulairesGuideDialog
        open={guideOuvert}
        onClose={() => setGuideOuvert(false)}
        onNouveau={indisponible ? undefined : () => { setGuideOuvert(false); openCreate(); }}
      />

      <FormulaireModeleDialog
        open={dialogOpen}
        modele={selected}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      {/* Aperçu interactif du modèle (monté dès qu'un modèle a été relu ; la définition reste pendant la fermeture) */}
      {modeleTest && (
        <TesterFormulaireDialog
          open={testOuvert}
          definition={modeleTest.definition}
          nomModele={modeleTest.nom}
          onClose={() => setTestOuvert(false)}
        />
      )}

      {/* Confirmation de suppression */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} fullScreen={isMobile}>
        <DialogTitle>Confirmez la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer le formulaire « {confirmDelete ? confirmDelete.nom : ''} » ? Les formulaires déjà envoyés ne sont
            pas affectés.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="primary">Annuler</Button>
          <Button onClick={handleDelete} color="error" autoFocus>Confirmer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default FormulairesScreen;
